import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import { parseToHtml } from '@gem/modules/markdown';
import { ICommonPopupsProps } from '@gem/popups';
import { MAX_ITEMS_PER_ROW, MAX_ROW_COUNT } from '@gem/utils';
import { encodeHtml, replaceHtmlTagsToSpecialSymbols } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { GroupType } from 'APP/model/group/groupModel.types';
import { PayloadType } from 'APP/model/message/messageModel.types';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';
import { AlbumPayload } from 'STORE/Messages/Message/Payload/AlbumPayload/AlbumPayload';

import { TMediaMessagePayload, TMediaItemPayload } from './MediaMessagePopup.types';
import { getFilePayload, isMediaPayload, getAlbumPayload } from './MediaMessagePopup.utils';
import { editMediaMessage } from './Tasks/editMediaMessage';
import { sendMediaMessages } from './Tasks/sendMediaMessages';

interface IMediaMessagePayloadData {
  id: string;
  payload: TMediaMessagePayload;
}

interface IMediaMessagePopupPresenter {
  group: Group | null;
  payloads: TMediaItemPayload[];
  isLoading: boolean;
  isEditMode: boolean;
  isShowSubmitMenu: boolean;
  mustBeScheduled: boolean;
  caption: string;
  editMessage: ChatMessage | null;
  messagesPayloads: TMediaMessagePayload[];
  messagesPayloadsData: IMediaMessagePayloadData[];
  popupTitle: string;
  canBeRemove: boolean;
  currentCaption: string;
  currentComment: string;
  successTitle: string;
  isSendMediaAsFile: boolean;
  isRecalculateProcess: boolean;
  isShowSendMediaAsFile: boolean;
  canShowComment: boolean;
  init(caption: string): Promise<void>;
  startLoading(): void;
  onSubmit(isScheduled?: boolean): Promise<void>;
  onInputSubmit(): void;
  setPayloads(payloads: TMediaItemPayload[]): void;
  stopLoading(): void;
  onClose(isSaveValue?: boolean): void;
  onDelete(payload: TMediaItemPayload): void;
  onChange(value: string): void;
  onChangeSendAsFile(value: boolean): void;
  reCalcPayloadsAsFiles(): Promise<void>;
}

const ALBUM_MAX_SIZE = MAX_ROW_COUNT * MAX_ITEMS_PER_ROW;

export interface IMediaMessagePopupPresenterParams {
  files: File[];
  caption: string;
  groupId: string;
  messageId?: string;
}

export function useMediaMessagePopupPresenter({
  params,
  popupInstance,
}: ICommonPopupsProps<IMediaMessagePopupPresenterParams>): IMediaMessagePopupPresenter {
  const { t } = useTranslation();
  const { caption, files = [], messageId, groupId } = params!;

  const presenter = useLocalObservable<IMediaMessagePopupPresenter>(() => ({
    isEditMode: false,
    isLoading: false,
    caption: '',
    payloads: [],
    isSendMediaAsFile: false,
    isRecalculateProcess: false,

    async init(): Promise<void> {
      presenter.startLoading();

      if (messageId && !files.length) {
        if (!presenter.editMessage) {
          presenter.onClose();
          return;
        }

        presenter.caption = presenter.currentComment;
        presenter.isEditMode = true;

        const payloads = (presenter.editMessage.payload.payloads || [presenter.editMessage]).map(
          (x: ChatMessage): TMediaItemPayload => x.payload
        );

        presenter.setPayloads(payloads);
        presenter.stopLoading();

        return;
      }

      const payloads: TMediaItemPayload[] = await Promise.all(
        (files || []).map((file) => getFilePayload(file, file.name))
      );

      presenter.caption = parseToHtml(encodeHtml(caption));
      presenter.isEditMode = false;
      presenter.setPayloads(payloads);
      presenter.stopLoading();
    },

    get group(): Group | null {
      return Entities.GroupsStore.getGroupById(groupId);
    },

    get editMessage(): ChatMessage | null {
      if (!messageId || !presenter.group) {
        return null;
      }

      const message = presenter.group.messagesStore.getMessageById(messageId);
      if (!message) {
        return null;
      }

      const payloadTypes = [
        PayloadType.Album,
        PayloadType.Image,
        PayloadType.File,
        PayloadType.AudioMessage,
        PayloadType.Video,
      ];

      if (!payloadTypes.includes(message.payload.payloadType)) {
        return null;
      }

      return message;
    },

    startLoading(): void {
      presenter.isLoading = true;
    },

    stopLoading(): void {
      presenter.isLoading = false;
    },

    get canShowComment(): boolean {
      return presenter.group?.type !== GroupType.SecretGroup;
    },

    get mustBeScheduled(): boolean {
      return presenter.group instanceof Schedule;
    },

    get successTitle(): string {
      if (presenter.isEditMode) {
        return t('done');
      } else if (presenter.mustBeScheduled) {
        return t('common_schedule');
      }

      return t('send');
    },

    get popupTitle(): string {
      if (presenter.isEditMode) {
        return t('message_edit');
      }

      if (presenter.payloads.every(isMediaPayload)) {
        return t('media_popup_header', { 0: presenter.payloads.length });
      }
      return t('files_popup_header', { 0: presenter.payloads.length });
    },

    get isShowSubmitMenu(): boolean {
      return !presenter.isEditMode;
    },

    setPayloads(payloads: TMediaItemPayload[]): void {
      presenter.payloads = payloads;
    },

    async onClose(isSaveValue = true): Promise<void> {
      if (presenter.isEditMode && presenter.editMessage) {
        if (presenter.currentCaption !== presenter.currentComment) {
          const res = await Tasks.app.showConfirm({ text: t('common_discard_changes_alert') });
          if (!res) {
            return;
          }
        }
      }

      if (!presenter.isEditMode && isSaveValue) {
        Entities.InputPanelsStore.getById(groupId)?.setValue(presenter.caption);
      }

      popupInstance.close();
    },

    async onSubmit(isScheduled?: boolean): Promise<void> {
      const group =
        presenter.group instanceof Schedule ? presenter.group.parentGroup : presenter.group;
      let result = false;

      if (!group?.canSendMessages) {
        Tasks.app.showToast(t('moderation_disable_send_message'));
        return;
      }

      if (presenter.isEditMode) {
        if (!presenter.editMessage) {
          return;
        }

        if (presenter.currentCaption === presenter.currentComment) {
          popupInstance.close();
          return;
        }

        result = await editMediaMessage(
          presenter.editMessage,
          presenter.currentCaption,
          presenter.mustBeScheduled || !!isScheduled
        );
      } else {
        result = await sendMediaMessages(
          group.id,
          presenter.messagesPayloads,
          presenter.currentCaption,
          presenter.mustBeScheduled || !!isScheduled,
          presenter.group?.inputPanel.replyMessage || undefined,
          presenter.group?.inputPanel.quote || undefined
        );
      }

      if (result) {
        presenter.onClose(false);
      }
    },

    onInputSubmit(): void {
      presenter.onSubmit(false);
    },

    onChange(value: string): void {
      presenter.caption = value;
    },

    onChangeSendAsFile(value: boolean): void {
      presenter.isSendMediaAsFile = value;
      presenter.reCalcPayloadsAsFiles();
    },

    get currentComment(): string {
      return presenter.editMessage?.payload.comment || '';
    },

    get currentCaption(): string {
      return replaceHtmlTagsToSpecialSymbols(presenter.caption).trim();
    },

    get canBeRemove(): boolean {
      if (presenter.isEditMode) {
        return false;
      }
      return true;
    },

    get isShowSendMediaAsFile(): boolean {
      if (presenter.isEditMode) {
        return false;
      }

      return (
        presenter.isSendMediaAsFile || presenter.payloads.some((payload) => isMediaPayload(payload))
      );
    },

    onDelete(payload: TMediaItemPayload): void {
      presenter.payloads = presenter.payloads.filter((item): boolean => {
        if (item.userFile && payload.userFile) {
          return item.userFile !== payload.userFile;
        }

        return item.url !== payload.url;
      });

      if (presenter.payloads.length === 0) {
        presenter.onClose();
      }
    },

    get messagesPayloads(): TMediaMessagePayload[] {
      if (presenter.isLoading) {
        return [];
      }

      if (presenter.isEditMode && presenter.editMessage) {
        return [presenter.editMessage.payload];
      }

      // Splitting files into messages.
      // Media files are grouped into albums and other files by separate messages
      const messagePayloads: Array<TMediaItemPayload[]> = [];
      let isLastMedia = false;
      let lastIndex = -1;

      presenter.payloads.forEach((payload: TMediaItemPayload): void => {
        if (!isMediaPayload(payload) || presenter.isSendMediaAsFile) {
          lastIndex++;
          isLastMedia = false;
          messagePayloads[lastIndex] = [payload];

          return;
        }

        lastIndex = isLastMedia ? lastIndex : lastIndex + 1;
        isLastMedia = true;
        messagePayloads[lastIndex] = messagePayloads[lastIndex] || [];

        if (messagePayloads[lastIndex].length < ALBUM_MAX_SIZE) {
          messagePayloads[lastIndex].push(payload);
        } else {
          lastIndex++;
          messagePayloads[lastIndex] = [payload];
        }
      });

      return messagePayloads.map((payloads): TMediaMessagePayload => {
        if (payloads.length === 1) {
          return payloads[0];
        }

        return getAlbumPayload(payloads);
      });
    },

    get messagesPayloadsData(): IMediaMessagePayloadData[] {
      return presenter.messagesPayloads.map((payload): IMediaMessagePayloadData => {
        if (payload instanceof AlbumPayload) {
          return { id: payload.payloads[0]!.id, payload };
        }

        return { id: `${payload.fileName}-${payload.fileSize}`, payload };
      });
    },

    async reCalcPayloadsAsFiles(): Promise<void> {
      if (presenter.isEditMode) {
        return;
      }

      presenter.isRecalculateProcess = true;

      const payloads = await Promise.all(
        presenter.payloads.map(
          ({ userFile, fileName }): Promise<TMediaItemPayload> =>
            getFilePayload(userFile!, fileName || '', presenter.isSendMediaAsFile)
        )
      );

      presenter.setPayloads(payloads);
      presenter.isRecalculateProcess = false;
    },
  }));

  useEffect(() => {
    presenter.init(caption);
  }, []);

  useEffect(() => {
    if (presenter.isEditMode && !presenter.editMessage) {
      presenter.onClose();
    }
  }, [presenter.editMessage, presenter.isEditMode]);

  useEffect(() => {
    if (!Entities.GroupsStore.isLoading && !presenter.group) {
      popupInstance.close();
    }
  }, [presenter.group, Entities.GroupsStore.isLoading]);

  return presenter;
}
